// @ts-nocheck
import { getProductCollectionRealTimeInventory } from "@insite/client-framework/Services/ProductServiceV2";
import { ProductInventoryDto } from "@insite/client-framework/Types/ApiModels";
import { postValveBomPrices } from "@store/Framework/Services/ValveConfiguratorService";
import { useEffect, useRef, useState } from "react";

export interface BomPriceInfo {
    unitNetPrice: number;
    unitNetPriceDisplay: string;
    /** Suggested (list) price returned by ERP when it exceeds the net; used for the "was" hint. */
    unitListPrice?: number;
    unitListPriceDisplay?: string;
    /** Currency returned by ERP alongside the prices. */
    currency?: string;
    /**
     * Kept for backward compatibility with widget consumers. The BOM pricing
     * endpoint no longer returns a catalog image; the widget falls back to the
     * `previewImageUrl` returned by /configure.
     */
    imagePath: string;
    /** Optional product detail path so the widget can render a clickable link. */
    productDetailPath?: string;
    /** Catalog product Id when the PN resolved to a real product. */
    productId?: string;
    /** Catalog product display name. */
    name?: string;
    /** Real-time inventory DTO for the resolved product. Best-effort — undefined when the API call failed or the PN did not resolve. */
    inventory?: ProductInventoryDto;
}

export interface BomPricingState {
    loading: boolean;
    error: string | null;
    /** Keyed by part number (uppercased, trimmed). Only entries with a resolved productId AND non-null unitNetPrice appear here. */
    prices: Map<string, BomPriceInfo>;
    /**
     * Info for the Unit PN when it was supplied. Populated whether or not the
     * Unit resolves to a catalog product or has a price, so the widget can
     * distinguish orderable vs quote-required states.
     */
    unit?: BomPriceInfo;
}

const requestKey = (partNumbers: string[]) =>
    [...new Set(partNumbers.map(p => p.trim().toUpperCase()).filter(Boolean))].sort().join("\u0001");

interface Bundle {
    /** All entries returned by the server, keyed by uppercased PN — includes not-in-catalog and unpriced rows. */
    raw: Map<string, BomPriceInfo>;
}

const inflight = new Map<string, Promise<Bundle>>();

async function fetchBomBundle(partNumbers: string[]): Promise<Bundle> {
    const cleaned = [...new Set(partNumbers.map(p => p.trim()).filter(Boolean))];
    const raw = new Map<string, BomPriceInfo>();
    if (cleaned.length === 0) {
        return { raw };
    }

    const priceResp = await postValveBomPrices({
        partNumbers: cleaned,
        unitOfMeasure: "EA",
        qtyOrdered: 1,
    });

    priceResp.prices?.forEach(row => {
        const pn = (row.partNumber || "").trim().toUpperCase();
        if (!pn) {
            return;
        }
        raw.set(pn, {
            unitNetPrice: row.unitNetPrice ?? 0,
            unitNetPriceDisplay: row.unitNetPriceDisplay || "",
            unitListPrice: row.unitListPrice ?? undefined,
            unitListPriceDisplay: row.unitListPriceDisplay ?? undefined,
            currency: row.currency ?? undefined,
            imagePath: "",
            productDetailPath: row.productDetailPath || undefined,
            productId: row.productId || undefined,
            name: row.name || undefined,
        });
    });

    const productIds = Array.from(raw.values())
        .map(p => p.productId)
        .filter((id): id is string => !!id);
    if (productIds.length > 0) {
        try {
            const invResp = await getProductCollectionRealTimeInventory({ productIds });
            const byId = new Map<string, ProductInventoryDto>();
            invResp.realTimeInventoryResults?.forEach(dto => byId.set(dto.productId, dto));
            raw.forEach(info => {
                if (info.productId) {
                    info.inventory = byId.get(info.productId);
                }
            });
        } catch {
            // Inventory is best-effort — pricing has already been loaded.
        }
    }

    return { raw };
}

function fetchBundleDeduped(partNumbers: string[]): Promise<Bundle> {
    const key = requestKey(partNumbers);
    const existing = inflight.get(key);
    if (existing) {
        return existing;
    }
    const p = fetchBomBundle(partNumbers);
    inflight.set(key, p);
    p.finally(() => inflight.delete(key));
    return p;
}

function buildPricesMap(raw: Map<string, BomPriceInfo>): Map<string, BomPriceInfo> {
    // Backward-compat contract: only surface entries where BOTH catalog resolved
    // AND ERP returned a non-zero price. Rule 3/4 (quote-required) rows have
    // unitNetPrice == 0 and are intentionally hidden here.
    const priced = new Map<string, BomPriceInfo>();
    raw.forEach((info, pn) => {
        if (info.productId && info.unitNetPrice > 0) {
            priced.set(pn, info);
        }
    });
    return priced;
}

/**
 * Loads real-time pricing (and best-effort inventory) for the supplied part
 * numbers via the configurator's bomPrices endpoint. Returns a map keyed by
 * uppercased part number containing entries that were priced. Optional
 * `unitPartNumber` is priced alongside and surfaced separately via `unit` so the
 * caller can distinguish orderable / quote-required states.
 */
export function useBomPricing(partNumbers: string[], unitPartNumber?: string): BomPricingState {
    const combined = unitPartNumber ? [unitPartNumber, ...partNumbers] : partNumbers;
    const key = requestKey(combined);
    const [state, setState] = useState<BomPricingState>({
        loading: combined.length > 0,
        error: null,
        prices: new Map(),
    });

    // `combined` / `unitPartNumber` are read through refs so they can stay out of
    // the effect deps. Including the array would re-run (and therefore cancel)
    // the effect on every render, aborting the in-flight fetch before it resolves.
    const combinedRef = useRef(combined);
    combinedRef.current = combined;
    const unitPnRef = useRef(unitPartNumber);
    unitPnRef.current = unitPartNumber;

    useEffect(() => {
        if (!key) {
            setState({ loading: false, error: null, prices: new Map() });
            return;
        }
        let cancelled = false;
        setState(s => ({ ...s, loading: true, error: null }));
        fetchBundleDeduped(combinedRef.current)
            .then(bundle => {
                if (cancelled) {
                    return;
                }
                const unitKey = (unitPnRef.current || "").trim().toUpperCase();
                const unit = unitKey ? bundle.raw.get(unitKey) : undefined;
                setState({
                    loading: false,
                    error: null,
                    prices: buildPricesMap(bundle.raw),
                    unit,
                });
            })
            .catch((err: unknown) => {
                if (cancelled) {
                    return;
                }
                const message = err instanceof Error ? err.message : "Failed to load pricing";
                setState({ loading: false, error: message, prices: new Map() });
            });

        return () => {
            cancelled = true;
        };
    }, [key]);

    return state;
}
